import React, { useEffect, useMemo, useState } from 'react';
import { Card, Tabs, Table, Button, Space, Form, Input, Select, Switch, Modal, message, Tag, Alert } from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import request from '../../../utils/request';

const { TabPane } = Tabs;
const { Option } = Select;

type PermissionResource = {
  id: number;
  key: string;
  name: string;
  category?: string;
  description?: string;
  is_active: boolean;
  model_label?: string;
  scope_rules?: any;
};

type PermissionAction = {
  id: number;
  key: string;
  name: string;
  description?: string;
  is_active: boolean;
};

type ResourceAction = {
  id: number;
  resource: number;
  resource_key?: string;
  action: number;
  action_key?: string;
  allowed_scopes: string[];
  is_enabled: boolean;
};

const SCOPES = [
  { value: 'global', label: '全局' },
  { value: 'assigned', label: '分配' },
  { value: 'owned', label: '拥有' },
  { value: 'none', label: '无权限' },
];

const ResourceActionManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('resources');
  const [loading, setLoading] = useState<boolean>(false);
  const [resources, setResources] = useState<PermissionResource[]>([]);
  const [actions, setActions] = useState<PermissionAction[]>([]);
  const [resourceActions, setResourceActions] = useState<ResourceAction[]>([]);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  // 资源-动作映射表格分页（受控）
  const [mappingPagination, setMappingPagination] = useState<{ current: number; pageSize: number }>({ current: 1, pageSize: 20 });
  // 按资源筛选（基于资源ID）
  const [mappingResourceFilter, setMappingResourceFilter] = useState<number | undefined>(undefined);

  // Modal state
  const [resourceModal, setResourceModal] = useState<{ visible: boolean; editing?: PermissionResource | null }>({ visible: false });
  const [actionModal, setActionModal] = useState<{ visible: boolean; editing?: PermissionAction | null }>({ visible: false });
  const [mappingModal, setMappingModal] = useState<{ visible: boolean; editing?: ResourceAction | null }>({ visible: false });

  const [resourceForm] = Form.useForm<PermissionResource & { scope_rules_text?: string }>();
  const [actionForm] = Form.useForm<PermissionAction>();
  const [mappingForm] = Form.useForm<{ resource: number; action: number; allowed_scopes: string[]; is_enabled: boolean }>();
  const [mappingGenerating, setMappingGenerating] = useState<boolean>(false);

  // Wizard state for scope_rules visual editor
  const [introspectLoading, setIntrospectLoading] = useState<boolean>(false);
  const [introspectFields, setIntrospectFields] = useState<string[]>([]);
  const [presetSources, setPresetSources] = useState<string[]>([]);
  const [wizardOwnedField, setWizardOwnedField] = useState<string | undefined>();
  const [wizardOwnedValueType, setWizardOwnedValueType] = useState<'user' | 'custom'>('user');
  const [wizardOwnedValueCustom, setWizardOwnedValueCustom] = useState<string>('');
  const [wizardAssignedField, setWizardAssignedField] = useState<string | undefined>();
  const [wizardAssignedSourceType, setWizardAssignedSourceType] = useState<'preset' | 'custom'>('preset');
  const [wizardAssignedSource, setWizardAssignedSource] = useState<string | undefined>();
  const [scopeToValidate, setScopeToValidate] = useState<'global' | 'owned' | 'assigned'>('assigned');
  const [userIdToValidate, setUserIdToValidate] = useState<string>('');
  const [validateLoading, setValidateLoading] = useState<boolean>(false);

  const base = '/api/v1/permission-management';

  // 通用：获取分页接口全量数据（兼容 DRF 分页 与 非分页返回）
  const fetchAllPages = async (path: string): Promise<any[]> => {
    let url: string | null = `${base}${path}`;
    const items: any[] = [];
    let guard = 0;
    while (url && guard < 100) {
      // 允许 next 返回绝对地址，直接请求
      const resp = await request.get(url);
      const data = resp.data;
      if (Array.isArray(data)) {
        items.push(...data);
        url = null;
      } else {
        if (Array.isArray(data.results)) items.push(...data.results);
        url = data.next || null;
      }
      guard += 1;
    }
    return items;
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [allResources, allActions, allMappings] = await Promise.all([
        fetchAllPages('/resources/'),
        fetchAllPages('/actions/'),
        fetchAllPages('/resource-actions/'),
      ]);
      setResources(allResources);
      setActions(allActions);
      setResourceActions(allMappings);
      // 数据变化后重置到第一页，避免页码溢出
      setMappingPagination((p) => ({ ...p, current: 1 }));
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const onRefresh = () => setRefreshKey((k) => k + 1);

  // Resources CRUD
  const openCreateResource = () => {
    resourceForm.resetFields();
    // 清空向导状态，避免沿用上次配置
    clearWizardState();
    setResourceModal({ visible: true, editing: null });
  };
  const openEditResource = (rec: PermissionResource) => {
    resourceForm.setFieldsValue({
      ...rec,
      // 显示为可编辑 JSON 文本
      scope_rules: undefined,
      scope_rules_text: rec.scope_rules ? JSON.stringify(rec.scope_rules, null, 2) : '',
    } as any);
    setResourceModal({ visible: true, editing: rec });
    // 尝试把已有 JSON 载入向导
    setTimeout(() => {
      try {
        const text = rec.scope_rules ? JSON.stringify(rec.scope_rules) : '';
        if (text) {
          loadJsonToWizard(rec.scope_rules);
        }
      } catch (e) {
        // ignore
      }
    }, 0);
  };

  const clearWizardState = () => {
    setWizardOwnedField(undefined);
    setWizardOwnedValueType('user');
    setWizardOwnedValueCustom('');
    setWizardAssignedField(undefined);
    setWizardAssignedSourceType('preset');
    setWizardAssignedSource(undefined);
    setIntrospectFields([]);
    setPresetSources([]);
    setScopeToValidate('assigned');
    setUserIdToValidate('');
  };
  const saveResource = async () => {
    const values: any = await resourceForm.validateFields();
    // 处理 scope_rules 文本
    if (typeof values.scope_rules_text === 'string') {
      const text = values.scope_rules_text.trim();
      if (text) {
        try {
          values.scope_rules = JSON.parse(text);
        } catch (e) {
          message.error('scope_rules 必须是有效的 JSON');
          return;
        }
      } else {
        values.scope_rules = {};
      }
    }
    delete values.scope_rules_text;

    try {
      if (resourceModal.editing) {
        await request.put(`${base}/resources/${resourceModal.editing.id}/`, values);
        message.success('已更新资源');
      } else {
        await request.post(`${base}/resources/`, values);
        message.success('已创建资源');
      }
      setResourceModal({ visible: false, editing: null });
      clearWizardState();
      onRefresh();
    } catch (e) {
      message.error('保存失败');
    }
  };
  const removeResource = async (rec: PermissionResource) => {
    Modal.confirm({
      title: `删除资源 ${rec.name}?`,
      onOk: async () => {
        try {
          await request.delete(`${base}/resources/${rec.id}/`);
          message.success('已删除');
          onRefresh();
        } catch (e) {
          message.error('删除失败');
        }
      },
    });
  };

  // Actions CRUD
  const openCreateAction = () => {
    actionForm.resetFields();
    setActionModal({ visible: true, editing: null });
  };
  const openEditAction = (rec: PermissionAction) => {
    actionForm.setFieldsValue(rec);
    setActionModal({ visible: true, editing: rec });
  };
  const saveAction = async () => {
    const values = await actionForm.validateFields();
    try {
      if (actionModal.editing) {
        await request.put(`${base}/actions/${actionModal.editing.id}/`, values);
        message.success('已更新动作');
      } else {
        await request.post(`${base}/actions/`, values);
        message.success('已创建动作');
      }
      setActionModal({ visible: false, editing: null });
      onRefresh();
    } catch (e) {
      message.error('保存失败');
    }
  };
  const removeAction = async (rec: PermissionAction) => {
    Modal.confirm({
      title: `删除动作 ${rec.name}?`,
      onOk: async () => {
        try {
          await request.delete(`${base}/actions/${rec.id}/`);
          message.success('已删除');
          onRefresh();
        } catch (e) {
          message.error('删除失败');
        }
      },
    });
  };

  // Mapping CRUD
  const openCreateMapping = () => {
    mappingForm.resetFields();
    mappingForm.setFieldsValue({ allowed_scopes: ['global'], is_enabled: true });
    setMappingModal({ visible: true, editing: null });
  };
  const openEditMapping = (rec: ResourceAction) => {
    mappingForm.setFieldsValue({
      resource: rec.resource,
      action: rec.action,
      allowed_scopes: rec.allowed_scopes,
      is_enabled: rec.is_enabled,
    });
    setMappingModal({ visible: true, editing: rec });
  };
  const saveMapping = async () => {
    const values = await mappingForm.validateFields();
    try {
      if (mappingModal.editing) {
        await request.put(`${base}/resource-actions/${mappingModal.editing.id}/`, values);
        message.success('已更新映射');
      } else {
        await request.post(`${base}/resource-actions/`, values);
        message.success('已创建映射');
      }
      setMappingModal({ visible: false, editing: null });
      onRefresh();
    } catch (e) {
      message.error('保存失败');
    }
  };

  // 批量生成：为所有资源生成所有动作，范围包含所有四种
  const handleBatchGenerateMappings = async () => {
    if (mappingGenerating) return;
    const allScopeValues: string[] = SCOPES.map((s) => s.value);
    Modal.confirm({
      title: '批量生成资源-动作映射',
      content: '将为所有资源创建/更新四个标准动作（read/create/update/delete），并将允许范围设置为全局/拥有/分配/无权限。已存在的映射将被更新为允许全部范围并启用。是否继续？',
      okText: '开始生成',
      cancelText: '取消',
      onOk: async () => {
        setMappingGenerating(true);
        try {
          // 建立现有映射索引：resourceId:actionId -> mapping
          const existingMap = new Map<string, ResourceAction>();
          (resourceActions || []).forEach((m) => existingMap.set(`${m.resource}:${m.action}`, m));

          // 常见四个动作键（若动作字典不全，将跳过缺失动作）
          const actionKeyToId = new Map<string, number>();
          (actions || []).forEach((a) => actionKeyToId.set(a.key, a.id));
          const standardActions = ['read', 'create', 'update', 'delete'].filter((k) => actionKeyToId.has(k));

          let created = 0;
          let updated = 0;
          let failed = 0;

          for (const res of resources || []) {
            for (const actKey of standardActions) {
              const actId = actionKeyToId.get(actKey)!;
              const mapKey = `${res.id}:${actId}`;
              const payload = { resource: res.id, action: actId, allowed_scopes: allScopeValues, is_enabled: true };
              try {
                const existed = existingMap.get(mapKey);
                if (existed) {
                  await request.put(`${base}/resource-actions/${existed.id}/`, payload);
                  updated += 1;
                } else {
                  await request.post(`${base}/resource-actions/`, payload);
                  created += 1;
                }
              } catch (e) {
                failed += 1;
              }
            }
          }

          message.success(`批量生成完成：新增 ${created}，更新 ${updated}，失败 ${failed}`);
          onRefresh();
        } finally {
          setMappingGenerating(false);
        }
      }
    });
  };
  const removeMapping = async (rec: ResourceAction) => {
    Modal.confirm({
      title: `删除映射 ${rec.resource_key}.${rec.action_key}?`,
      onOk: async () => {
        try {
          await request.delete(`${base}/resource-actions/${rec.id}/`);
          message.success('已删除');
          onRefresh();
        } catch (e) {
          message.error('删除失败');
        }
      },
    });
  };

  // -------------- Scope Wizard helpers --------------
  const fetchIntrospection = async () => {
    const modelLabel = resourceForm.getFieldValue('model_label');
    if (!modelLabel) {
      message.warning('请先填写模型标识 model_label');
      return;
    }
    setIntrospectLoading(true);
    try {
      const resp = await request.get(`${base}/resources/introspect/`, {
        params: { model_label: modelLabel },
      });
      const fields: string[] = resp.data?.fields || [];
      const sources: string[] = resp.data?.preset_sources || [];
      setIntrospectFields(fields);
      setPresetSources(sources);
      if (!wizardAssignedSource && sources.length) setWizardAssignedSource(sources[0]);
      message.success('已载入模型字段');
    } catch (e) {
      message.error('字段探测失败');
    } finally {
      setIntrospectLoading(false);
    }
  };

  const buildScopeRulesFromWizard = () => {
    const owned: any = {};
    if (wizardOwnedField) {
      owned.field = wizardOwnedField;
      if (wizardOwnedValueType === 'user') {
        owned.value = 'user';
      } else if (wizardOwnedValueCustom) {
        owned.value = wizardOwnedValueCustom;
      }
    }
    const assigned: any = {};
    if (wizardAssignedField) {
      assigned.field = wizardAssignedField;
      if (wizardAssignedSourceType === 'preset' && wizardAssignedSource) {
        assigned.source = wizardAssignedSource;
      } else if (wizardAssignedSourceType === 'custom' && wizardAssignedSource) {
        assigned.source = wizardAssignedSource;
      }
    }
    const rules: any = {};
    if (Object.keys(owned).length) rules.owned = owned;
    if (Object.keys(assigned).length) rules.assigned = assigned;
    return rules;
  };

  const applyTemplate = (tpl: 'warehouse' | 'project' | 'owned_generic' | 'guess') => {
    const modelLabel: string | undefined = resourceForm.getFieldValue('model_label');
    let template: 'warehouse' | 'project' | 'owned_generic' = tpl as any;
    if (tpl === 'guess') {
      const ml = (modelLabel || '').toLowerCase();
      if (ml.includes('warehouse')) template = 'warehouse';
      else if (ml.includes('project')) template = 'project';
      else template = 'owned_generic';
    }
    if (template === 'warehouse') {
      setWizardAssignedField('id');
      setWizardAssignedSourceType('preset');
      setWizardAssignedSource('profile.assigned_warehouses');
    }
    if (template === 'project') {
      setWizardAssignedField('id');
      setWizardAssignedSourceType('preset');
      setWizardAssignedSource('profile.assigned_projects');
    }
    if (template === 'owned_generic') {
      setWizardOwnedField('created_by');
      setWizardOwnedValueType('user');
      setWizardOwnedValueCustom('');
    }
    message.success('已应用模板，请根据模型实际字段调整');
  };

  const loadJsonToWizard = (jsonVal?: any) => {
    let rules = jsonVal;
    if (!rules) {
      const text = resourceForm.getFieldValue('scope_rules_text');
      try {
        rules = text ? JSON.parse(text) : {};
      } catch (e) {
        message.error('上方 JSON 无法解析');
        return;
      }
    }
    try {
      const owned = rules?.owned || {};
      setWizardOwnedField(owned.field);
      if (owned.value === 'user' || owned.value === undefined) {
        setWizardOwnedValueType('user');
        setWizardOwnedValueCustom('');
      } else {
        setWizardOwnedValueType('custom');
        setWizardOwnedValueCustom(String(owned.value));
      }
      const assigned = rules?.assigned || {};
      setWizardAssignedField(assigned.field);
      if (assigned.source && (presetSources.includes(assigned.source) || introspectFields.length >= 0)) {
        setWizardAssignedSourceType(presetSources.includes(assigned.source) ? 'preset' : 'custom');
        setWizardAssignedSource(assigned.source);
      }
      message.success('已从 JSON 载入向导');
    } catch (e) {
      message.error('载入向导失败');
    }
  };

  const writeWizardToJson = () => {
    const rules = buildScopeRulesFromWizard();
    resourceForm.setFieldsValue({ scope_rules_text: JSON.stringify(rules, null, 2) } as any);
    message.success('已写回 JSON');
  };

  const validateScopeRules = async () => {
    const modelLabel = resourceForm.getFieldValue('model_label');
    if (!modelLabel) {
      message.warning('请先填写模型标识 model_label');
      return;
    }
    const rules = buildScopeRulesFromWizard();
    setValidateLoading(true);
    try {
      const resp = await request.post(`${base}/resources/validate-scope/`, {
        model_label: modelLabel,
        scope_rules: rules,
        scope: scopeToValidate,
        user_id: userIdToValidate || undefined,
      });
      const total = resp.data?.total_count ?? 0;
      message.success(`校验成功：总数量 ${total}`);
    } catch (e) {
      message.error('校验失败');
    } finally {
      setValidateLoading(false);
    }
  };

  // Tables
  const resourceColumns = [
    { title: 'Key', dataIndex: 'key' },
    { title: '名称', dataIndex: 'name' },
    { title: '分类', dataIndex: 'category' },
    { title: '模型', dataIndex: 'model_label' },
    { title: '状态', dataIndex: 'is_active', render: (v: boolean) => (v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>) },
    {
      title: '操作',
      render: (_: any, rec: PermissionResource) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditResource(rec)}>编辑</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeResource(rec)}>删除</Button>
        </Space>
      ),
    },
  ];

  const actionColumns = [
    { title: 'Key', dataIndex: 'key' },
    { title: '名称', dataIndex: 'name' },
    { title: '状态', dataIndex: 'is_active', render: (v: boolean) => (v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>) },
    {
      title: '操作',
      render: (_: any, rec: PermissionAction) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditAction(rec)}>编辑</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeAction(rec)}>删除</Button>
        </Space>
      ),
    },
  ];

  const mappingColumns = [
    { title: '资源', dataIndex: 'resource_key' },
    { title: '动作', dataIndex: 'action_key' },
    { title: '范围', dataIndex: 'allowed_scopes', render: (scopes: string[]) => scopes?.map((s) => <Tag key={s}>{s}</Tag>) },
    { title: '启用', dataIndex: 'is_enabled', render: (v: boolean) => (v ? <Tag color="green">是</Tag> : <Tag>否</Tag>) },
    {
      title: '操作',
      render: (_: any, rec: ResourceAction) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditMapping(rec)}>编辑</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeMapping(rec)}>删除</Button>
        </Space>
      ),
    },
  ];

  const resourceOptions = useMemo(() => resources.map((r) => ({ label: `${r.key} - ${r.name}`, value: r.id })), [resources]);
  const actionOptions = useMemo(() => actions.map((a) => ({ label: `${a.key} - ${a.name}`, value: a.id })), [actions]);
  const filteredResourceActions = useMemo(() => {
    if (!mappingResourceFilter) return resourceActions;
    return resourceActions.filter(m => m.resource === mappingResourceFilter);
  }, [resourceActions, mappingResourceFilter]);

  return (
    <Card>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="资源与动作 - 使用说明"
        description={
          <div>
            <div>1) 资源：定义可控模块并关联后台数据模型；动作：如查看/创建/更新/删除。</div>
            <div>2) 资源-动作映射：为每个组合选择允许的范围（全局/拥有/分配）。</div>
            <div>3) 在资源的“范围配置（向导）”中配置 Owned/Assigned 的字段与来源，并可一键校验。</div>
          </div>
        }
      />
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={onRefresh}>刷新</Button>
      </Space>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="资源" key="resources">
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateResource}>新增资源</Button>
          </Space>
          <Table rowKey="id" loading={loading} columns={resourceColumns as any} dataSource={resources} pagination={{ pageSize: 10 }} />
        </TabPane>
        <TabPane tab="动作" key="actions">
          <Space style={{ marginBottom: 12 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateAction}>新增动作</Button>
          </Space>
          <Table rowKey="id" loading={loading} columns={actionColumns as any} dataSource={actions} pagination={{ pageSize: 10 }} />
        </TabPane>
        <TabPane tab="资源-动作映射" key="mappings">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="允许范围 与 角色模板范围 的关系"
            description={
              <div>
                <div><strong>允许范围（本页）</strong>：为 资源×动作 设置可授予的范围白名单与总开关（上限/护栏）。</div>
                <div style={{ marginTop: 4 }}><strong>范围含义</strong>：全局=不过滤；分配=按分配数据过滤；拥有=按归属字段过滤；无权限=不开放该动作。</div>
                <div style={{ marginTop: 4 }}><strong>与角色模板关系</strong>：角色模板只能在“允许范围”内选择其一作为最终生效范围，超出会校验失败。</div>
              </div>
            }
          />
          <Space style={{ marginBottom: 12 }}>
            <Select
              allowClear
              showSearch
              placeholder="按资源筛选"
              style={{ minWidth: 260 }}
              value={mappingResourceFilter}
              onChange={(val) => { setMappingResourceFilter(val); setMappingPagination(p => ({ ...p, current: 1 })); }}
              optionFilterProp="label"
              options={resourceOptions}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateMapping}>新增映射</Button>
            <Button loading={mappingGenerating} onClick={handleBatchGenerateMappings}>批量生成</Button>
          </Space>
          <Table
            rowKey="id"
            loading={loading}
            columns={mappingColumns as any}
            dataSource={filteredResourceActions}
            pagination={{
              current: mappingPagination.current,
              pageSize: mappingPagination.pageSize,
              total: filteredResourceActions.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10','20','50','100'],
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, pageSize) => setMappingPagination({ current: page, pageSize: pageSize || mappingPagination.pageSize }),
              onShowSizeChange: (_, size) => setMappingPagination({ current: 1, pageSize: size })
            }}
          />
        </TabPane>
      </Tabs>

      {/* Resource Modal */}
      <Modal
        title={resourceModal.editing ? '编辑资源' : '新增资源'}
        open={resourceModal.visible}
        onCancel={() => { setResourceModal({ visible: false, editing: null }); clearWizardState(); }}
        onOk={saveResource}
        destroyOnClose
        width={920}
      >
        <Tabs defaultActiveKey="basic">
          <TabPane tab="基本" key="basic">
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="基本信息 - 使用说明"
              description={
                <div>
                  <div>1) Key 用于标识资源（英文/下划线），Name 为显示名称。</div>
                  <div>2) 如果仅授予 <b>global（全局）</b> 权限，可不填写“模型标识”和“范围规则”。</div>
                  <div>3) 只有在需要配置 <b>owned/assigned</b> 行级过滤时，才需要填写“模型标识”和“范围规则”；“范围配置（向导）”可帮助生成 JSON。</div>
                  <div>4) 模型标识建议在以下情况填写：该资源会用于列表页过滤，且资源 key 与实际模型的 app_label 不一致（便于 DataFilter 正确映射）。</div>
                </div>
              }
            />
            <Form form={resourceForm} layout="vertical">
              <Form.Item 
                label="Key" 
                name="key" 
                rules={[
                  { required: true, message: '请输入资源键' },
                  { pattern: /^[a-z0-9_-]+$/, message: 'Key 只能包含小写字母、数字、下划线(_)或中划线(-)，且不能包含空格' }
                ]}
                tooltip={
                  <div>
                    <div><strong>作用</strong>：资源的唯一英文标识，用于规则与代码引用</div>
                    <div><strong>格式</strong>：仅小写字母[a-z]、数字[0-9]、下划线(_)或中划线(-)，不允许空格</div>
                    <div><strong>示例</strong>：warehouse、inbound_order、project_group</div>
                  </div>
                }
              >
                <Input placeholder="如 warehouse" />
              </Form.Item>
              <Form.Item 
                label="名称" 
                name="name" 
                rules={[{ required: true, message: '请输入名称' }]}
                tooltip={
                  <div>
                    <div><strong>作用</strong>：中文显示名称，方便管理员识别</div>
                    <div><strong>示例</strong>：仓库、入库单</div>
                  </div>
                }
              >
                <Input placeholder="如 仓库" />
              </Form.Item>
              <Form.Item 
                label="分类" 
                name="category"
                tooltip={
                  <div>
                    <div><strong>作用</strong>：可选，用于将资源分组</div>
                    <div><strong>示例</strong>：仓储、财务</div>
                  </div>
                }
              >
                <Input placeholder="如 仓储" />
              </Form.Item>
              <Form.Item 
                label="描述" 
                name="description"
                tooltip={
                  <div>
                    <div><strong>作用</strong>：说明资源用途</div>
                    <div><strong>示例</strong>：控制仓库数据的访问权限</div>
                  </div>
                }
              >
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item 
                label="模型标识 model_label" 
                name="model_label" 
                tooltip={
                  <div>
                    <div><strong>作用</strong>：对应后端 Django 的 app_label.ModelName，用于字段探测与规则过滤</div>
                    <div><strong>示例</strong>：warehouse.Warehouse、project.Project</div>
                  </div>
                }
              >
                <Input placeholder="app_label.ModelName，例如 warehouse.Warehouse" />
              </Form.Item>
              <Form.Item 
                label="范围规则 scope_rules (JSON)" 
                name="scope_rules_text" 
                tooltip={
                  <div>
                    <div><strong>作用</strong>：通过 JSON 定义数据范围（Owned/Assigned），也可用“范围配置（向导）”生成</div>
                    <div>
                      <strong>示例</strong>：
                      <code style={{ marginLeft: 4 }}>
                        {"{\"assigned\": {\"field\": \"id\", \"source\": \"profile.assigned_warehouses\"}}"}
                      </code>
                    </div>
                  </div>
                }
              >
                <Input.TextArea rows={8} placeholder='{"assigned":{"field":"id","source":"profile.assigned_warehouses"}}' />
              </Form.Item>
              <Form.Item label="启用" name="is_active" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Form>
          </TabPane>
          <TabPane tab="范围配置（向导）" key="wizard">
            {/* 当前资源信息提示 */}
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <div>
                  当前资源：{
                    resourceModal.editing
                      ? `${resourceModal.editing.name || resourceModal.editing.key} (${resourceModal.editing.key})`
                      : (resourceForm.getFieldValue('name') || resourceForm.getFieldValue('key'))
                        ? `${resourceForm.getFieldValue('name') || resourceForm.getFieldValue('key')} (${resourceForm.getFieldValue('key') || '-'})`
                        : '新增（未保存）'
                  }
                </div>
              }
              description={<div>为 Owned/Assigned 配置字段和来源；保存资源后生效。</div>}
            />
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="配置向导 - 使用说明"
              description={
                <div>
                  <div>1) 先“载入模型字段”，再在 Owned/Assigned 区域选择字段与来源（可用预置模板）。</div>
                  <div>2) 使用“规则校验”，可填测试用户 ID 验证样本数量是否大于 0。</div>
                  <div>3) 点击“写回上方 JSON”，返回“基本”页签保存资源即可生效。</div>
                </div>
              }
            />
            <div style={{ marginBottom: 12 }}>
              <Space style={{ marginBottom: 8 }} wrap>
                <Button loading={introspectLoading} onClick={fetchIntrospection}>载入模型字段</Button>
                <Button onClick={() => loadJsonToWizard()}>从上方 JSON 读取</Button>
                <Button type="primary" onClick={writeWizardToJson}>写回上方 JSON</Button>
              </Space>
              <Space wrap>
                <Button onClick={() => applyTemplate('warehouse')}>仓库模板（id in assigned_warehouses）</Button>
                <Button onClick={() => applyTemplate('project')}>项目模板（id in assigned_projects）</Button>
                <Button onClick={() => applyTemplate('owned_generic')}>通用 Owned（created_by = user）</Button>
              </Space>
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <Card size="small" title="Owned 拥有者过滤">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <div style={{ marginBottom: 6 }}>字段</div>
                      <Select
                        showSearch
                        value={wizardOwnedField}
                        onChange={setWizardOwnedField as any}
                        placeholder="选择如 created_by / owner / user 等字段"
                        style={{ width: '100%' }}
                        options={(introspectFields || []).map((f) => ({ label: f, value: f }))}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 6 }}>值类型</div>
                      <Select
                        value={wizardOwnedValueType}
                        onChange={setWizardOwnedValueType as any}
                        options={[
                          { label: '当前用户(user)', value: 'user' },
                          { label: '自定义值', value: 'custom' },
                        ]}
                        style={{ width: '100%' }}
                      />
                    </div>
                    {wizardOwnedValueType === 'custom' && (
                      <div>
                        <div style={{ marginBottom: 6 }}>自定义值</div>
                        <Input value={wizardOwnedValueCustom} onChange={(e) => setWizardOwnedValueCustom(e.target.value)} placeholder="如 某个固定 user_id 或用户名" />
                      </div>
                    )}
                  </Space>
                </Card>
              </div>
              <div style={{ flex: 1 }}>
                <Card size="small" title="Assigned 分配过滤">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <div style={{ marginBottom: 6 }}>字段</div>
                      <Select
                        showSearch
                        value={wizardAssignedField}
                        onChange={setWizardAssignedField as any}
                        placeholder="选择如 project_id / warehouse_id 等字段"
                        style={{ width: '100%' }}
                        options={(introspectFields || []).map((f) => ({ label: f, value: f }))}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 6 }}>来源类型</div>
                      <Select
                        value={wizardAssignedSourceType}
                        onChange={setWizardAssignedSourceType as any}
                        options={[
                          { label: '预置', value: 'preset' },
                          { label: '自定义', value: 'custom' },
                        ]}
                        style={{ width: '100%' }}
                      />
                    </div>
                    {wizardAssignedSourceType === 'preset' ? (
                      <div>
                        <div style={{ marginBottom: 6 }}>预置来源</div>
                        <Select
                          showSearch
                          value={wizardAssignedSource}
                          onChange={setWizardAssignedSource as any}
                          placeholder="选择如 profile.assigned_projects / profile.assigned_warehouses"
                          style={{ width: '100%' }}
                          options={(presetSources || []).map((s) => ({ label: s, value: s }))}
                        />
                      </div>
                    ) : (
                      <div>
                        <div style={{ marginBottom: 6 }}>自定义来源路径</div>
                        <Input value={wizardAssignedSource} onChange={(e) => setWizardAssignedSource(e.target.value)} placeholder="如 profile.assigned_xxx" />
                      </div>
                    )}
                  </Space>
                </Card>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Card size="small" title="JSON 预览（只读）">
                <Input.TextArea
                  value={JSON.stringify(buildScopeRulesFromWizard(), null, 2)}
                  readOnly
                  rows={10}
                />
              </Card>
            </div>
            <div style={{ marginTop: 12 }}>
              <Card size="small" title="规则校验">
                <Space wrap>
                  <span>校验范围</span>
                  <Select
                    value={scopeToValidate}
                    onChange={setScopeToValidate as any}
                    options={[
                      { label: 'assigned', value: 'assigned' },
                      { label: 'owned', value: 'owned' },
                      { label: 'global', value: 'global' },
                    ]}
                    style={{ width: 160 }}
                  />
                  <Input
                    placeholder="可选：测试 user_id"
                    style={{ width: 220 }}
                    value={userIdToValidate}
                    onChange={(e) => setUserIdToValidate(e.target.value)}
                  />
                  <Button type="primary" loading={validateLoading} onClick={validateScopeRules}>校验规则</Button>
                </Space>
              </Card>
            </div>
          </TabPane>
        </Tabs>
      </Modal>

      {/* Action Modal */}
      <Modal
        title={actionModal.editing ? '编辑动作' : '新增动作'}
        open={actionModal.visible}
        onCancel={() => setActionModal({ visible: false, editing: null })}
        onOk={saveAction}
        destroyOnClose
      >
        <Form form={actionForm} layout="vertical">
          <Form.Item label="Key" name="key" rules={[{ required: true, message: '请输入动作键（如 read/create/update/delete）' }]}>
            <Input placeholder="如 read" />
          </Form.Item>
          <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如 查看" />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="启用" name="is_active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Mapping Modal */}
      <Modal
        title={mappingModal.editing ? '编辑资源-动作映射' : '新增资源-动作映射'}
        open={mappingModal.visible}
        onCancel={() => setMappingModal({ visible: false, editing: null })}
        onOk={saveMapping}
        destroyOnClose
      >
        <Form form={mappingForm} layout="vertical">
          <Form.Item label="资源" name="resource" rules={[{ required: true, message: '请选择资源' }]}>
            <Select placeholder="选择资源">
              {resourceOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="动作" name="action" rules={[{ required: true, message: '请选择动作' }]}>
            <Select placeholder="选择动作">
              {actionOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="允许范围" name="allowed_scopes" rules={[{ required: true, message: '请选择允许的范围' }]}>
            <Select mode="multiple" placeholder="选择允许的范围">
              {SCOPES.map((s) => (
                <Option key={s.value} value={s.value}>{s.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="启用" name="is_enabled" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ResourceActionManager;


